import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Wrench, 
  Layers, 
  Zap, 
  Paintbrush, 
  Home, 
  HardHat,
  ArrowUp,
  Palette,
  Package,
  ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available icons for selection
const ICON_OPTIONS = [
  { value: 'Wrench', label: 'Wrench', icon: Wrench },
  { value: 'Layers', label: 'Layers', icon: Layers },
  { value: 'Zap', label: 'Zap (Electric)', icon: Zap },
  { value: 'Paintbrush', label: 'Paintbrush', icon: Paintbrush },
  { value: 'Home', label: 'Home', icon: Home },
  { value: 'HardHat', label: 'Hard Hat', icon: HardHat },
  { value: 'ArrowUp', label: 'Arrow Up', icon: ArrowUp },
  { value: 'Palette', label: 'Palette', icon: Palette },
  { value: 'Package', label: 'Package', icon: Package },
];

const COLOR_OPTIONS = [
  { value: 'bg-blue-500', label: 'Blue' },
  { value: 'bg-slate-500', label: 'Slate' },
  { value: 'bg-yellow-500', label: 'Yellow' },
  { value: 'bg-green-500', label: 'Green' },
  { value: 'bg-orange-500', label: 'Orange' },
  { value: 'bg-gray-600', label: 'Gray' },
  { value: 'bg-purple-500', label: 'Purple' },
  { value: 'bg-pink-500', label: 'Pink' },
  { value: 'bg-red-500', label: 'Red' },
  { value: 'bg-teal-500', label: 'Teal' },
];

const iconMap = {
  Wrench, Layers, Zap, Paintbrush, Home, HardHat, ArrowUp, Palette, Package
};

export default function TradeManagement() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [editingTrade, setEditingTrade] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Wrench',
    color: 'bg-blue-500',
    image_url: ''
  });

  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${API}/trades?include_inactive=true`);
      setTrades(response.data);
    } catch (error) {
      toast.error('Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setEditingTrade(null);
    setFormData({
      name: '',
      description: '',
      icon: 'Wrench',
      color: 'bg-blue-500',
      image_url: ''
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (trade) => {
    setIsEditing(true);
    setEditingTrade(trade);
    setFormData({
      name: trade.name,
      description: trade.description,
      icon: trade.icon || 'Wrench',
      color: trade.color || 'bg-blue-500',
      image_url: trade.image_url || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Trade name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/trades/${editingTrade.trade_id}`, formData);
        toast.success('Trade updated successfully');
      } else {
        await axios.post(`${API}/trades`, formData);
        toast.success('Trade created successfully');
      }
      setDialogOpen(false);
      fetchTrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save trade');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (trade) => {
    try {
      await axios.put(`${API}/trades/${trade.trade_id}`, {
        is_active: !trade.is_active
      });
      toast.success(`Trade ${trade.is_active ? 'deactivated' : 'activated'}`);
      fetchTrades();
    } catch (error) {
      toast.error('Failed to update trade');
    }
  };

  const handleDelete = async (trade) => {
    if (!window.confirm(`Are you sure you want to deactivate "${trade.name}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/trades/${trade.trade_id}`);
      toast.success('Trade deactivated');
      fetchTrades();
    } catch (error) {
      toast.error('Failed to delete trade');
    }
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Package;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="trade-management-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">
                Trade Management
              </h1>
              <p className="text-muted-foreground mt-1">
                Add, edit, or deactivate trade templates
              </p>
            </div>
            <Button onClick={handleOpenCreate} data-testid="add-trade-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Trade
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              All Trades ({trades.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Icon</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trades.map(trade => {
                    const Icon = getIconComponent(trade.icon);
                    return (
                      <TableRow key={trade.trade_id} data-testid={`trade-row-${trade.trade_id}`}>
                        <TableCell>
                          <div className={`w-10 h-10 ${trade.color} rounded-lg flex items-center justify-center`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{trade.name}</TableCell>
                        <TableCell className="text-muted-foreground max-w-xs truncate">
                          {trade.description}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={trade.is_active}
                              onCheckedChange={() => handleToggleActive(trade)}
                              data-testid={`toggle-${trade.trade_id}`}
                            />
                            <Badge variant={trade.is_active ? 'default' : 'secondary'}>
                              {trade.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEdit(trade)}
                              data-testid={`edit-${trade.trade_id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(trade)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-${trade.trade_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {isEditing ? 'Edit Trade' : 'Add New Trade'}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update the trade details below' : 'Fill in the details for the new trade template'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="trade-name">Trade Name *</Label>
              <Input
                id="trade-name"
                placeholder="e.g., HVAC, Glazing, Scaffolding"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="trade-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trade-description">Description *</Label>
              <Input
                id="trade-description"
                placeholder="e.g., Heating, ventilation, air conditioning"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="trade-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(value) => setFormData({ ...formData, icon: value })}
                >
                  <SelectTrigger data-testid="icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(opt => {
                      const IconComp = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <IconComp className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({ ...formData, color: value })}
                >
                  <SelectTrigger data-testid="color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${opt.value}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="image-url">Image URL (Optional)</Label>
              <Input
                id="image-url"
                placeholder="https://example.com/image.jpg"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                data-testid="image-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Use Unsplash or Pexels for free trade-related images
              </p>
            </div>
            {/* Preview */}
            <div className="pt-4 border-t">
              <Label className="mb-2 block">Preview</Label>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 ${formData.color} rounded-lg flex items-center justify-center`}>
                  {(() => {
                    const PreviewIcon = getIconComponent(formData.icon);
                    return <PreviewIcon className="h-6 w-6 text-white" />;
                  })()}
                </div>
                <div>
                  <p className="font-medium">{formData.name || 'Trade Name'}</p>
                  <p className="text-sm text-muted-foreground">{formData.description || 'Description'}</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-trade-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                isEditing ? 'Update Trade' : 'Create Trade'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
